import { createContext, useContext, type ReactNode } from "react";

// A level is a Submenu's rows or a MenuPanel's content, shown in place of the menu's rows with a Back row above.
// Phones show all submenus as levels.

export type MenuLevel = {
    id: string;
    /** Shown in the Back row while the level is open. */
    label: ReactNode;
};

/** The last level entered or left, used to move focus once it renders. */
export type LevelChange = {
    levelId: string;
    direction: "entered" | "left";
    /** Rows take focus only on keyboard changes, since focus also highlights them. Otherwise the menu takes focus. */
    byKeyboard: boolean;
};

type MenuLevels = {
    /** True on phones: submenus open as levels instead of beside the menu. */
    submenusAsLevels: boolean;
    /** Outermost first, and the last is showing. */
    openLevels: MenuLevel[];
    openLevel: (level: MenuLevel, how: { byKeyboard: boolean }) => void;
    back: (how?: { byKeyboard: boolean }) => void;
    lastLevelChange: LevelChange | null;
    /** Focuses the menu without highlighting any row. */
    focusMenu: () => void;
    close: () => void;
    /** Element that open levels portal into, below the Back row. */
    levelContainer: HTMLElement | null;
};

export const MenuLevelsContext = createContext<MenuLevels | null>(null);

/** Used to get the current menu levels. The caller is named in the error when used outside a Menu. */
export function useMenuLevels(caller: "Submenu" | "MenuPanel" | "useMenuPanel()"): MenuLevels {
    const levels = useContext(MenuLevelsContext);
    if (levels === null) {
        throw new Error(`${caller} must be inside a Menu.`);
    }
    return levels;
}

/** For MenuPanel content: go back or close the menu. */
export function useMenuPanel() {
    const levels = useMenuLevels("useMenuPanel()");
    return { back: () => levels.back(), close: levels.close };
}
